import { useCallback, useEffect, useRef, useState } from "react";
import StyledButton, { ButtonSize } from "frontend/ui-components/styled-button";
import style from "./invite-to-account.module.css";
import tracking from "frontend/tracking";
import consts from "shared/consts";
import { useFeatureFlag } from "frontend/hooks/use-feature-flag/use-feature-flag";
import { useTrackUserTyping } from "frontend/hooks/use-track-user-typing";
import { trackInviteCompletedAccount } from "utils/invite-utils";
import { getPathPrefix } from "utils/getPathPrefix";
import InviteToAccountEmails from "./invite-to-account-emails";
import { AccountCreatedFromSource, Permission } from "shared/datamodel/schemas";
import { isEmail } from "utils/string-utils";
import { inviteUserNew } from "frontend/api";
import CheckCircledIcon from "frontend/icons/check-circled";
import { BUTTON_STYLE, DEFAULT_EMAILS, makeid } from "./invite-to-account-utils";
import useStateValue from "frontend/state/value";
import classNames from "classnames";
import { InviteToAccountErrorType } from "./types";

export function useTrackUserSelfInvited() {
  const eventAlreadyTriggered = useRef(false);

  return useCallback((value: string, userEmail: string) => {
    if (value === userEmail && !eventAlreadyTriggered.current) {
      tracking.trackAnalyticsEvent("invite_to_account_add_same_user_email");
      eventAlreadyTriggered.current = true;
    }
  }, []);
}

export default function InviteToAccount({ onCompleted }: { onCompleted: () => void }) {
  const [{ user }] = useStateValue();
  const isCreatedFromMonday = user?.account?.createdFromSource === AccountCreatedFromSource.Monday;
  const [emails, setEmails] = useState<Record<string, string>>(DEFAULT_EMAILS);
  const [isSending, setIsSending] = useState(false);
  const [invitedAmount, setInvitedAmount] = useState<number | null>(null);
  const invitedCompleted = invitedAmount !== null;
  const trackUserSelfInvited = useTrackUserSelfInvited();

  const isValidEmail = useCallback(
    (email: string) => {
      const isSameUserEmail = email === user?.email;
      return {
        isValid: isEmail(email) && !isSameUserEmail,
        errorType: isSameUserEmail ? InviteToAccountErrorType.SameUserEmail : InviteToAccountErrorType.InvalidEmail,
      };
    },
    [user?.email]
  );

  const isPerSeatLicenseEnabled = useFeatureFlag("per-seat-license");

  const isThereAnyValidEmail = Object.values(emails).some((email) => isValidEmail(email).isValid);

  const trackUserTyping = useTrackUserTyping();

  useEffect(() => {
    tracking.trackEvent(consts.TRACKING_CATEGORY.PAGE_VIEWS, "onboarding_invite_screen");
  }, []);

  const inviteClicked = useCallback(async () => {
    if (!isThereAnyValidEmail) {
      return;
    }
    const emailsToInvite = Object.values(emails).filter((email) => isValidEmail(email).isValid);
    tracking.trackEvent(
      consts.TRACKING_CATEGORY.ONBOARDING,
      "invite_contacts_button_clicked",
      "emails",
      emailsToInvite.length
    );
    setIsSending(true);
    try {
      const { failed, users, invitedToAccountAmount, invitedToAccountFailedAmount } = await inviteUserNew({
        bulkPermission: Permission.editor,
        emails: emailsToInvite,
        isPerSeatLicenseEnabled: isPerSeatLicenseEnabled ?? false,
        addAsRestricted: false,
      });
      if (failed.length > 0) {
        tracking.trackEvent(consts.TRACKING_CATEGORY.ONBOARDING, "invite_contacts_failed", "emails", failed.length);
      }
      if (users.length > 0) {
        tracking.trackEvent(consts.TRACKING_CATEGORY.ONBOARDING, "invite_contacts_success", "emails", users.length);
      }
      trackInviteCompletedAccount(invitedToAccountAmount, invitedToAccountFailedAmount, "emails");
      setInvitedAmount(users.length);
      setTimeout(() => {
        onCompleted();
      }, 1000);
    } catch (error) {
      console.error("failed invite", error);
    } finally {
      setIsSending(false);
    }
  }, [emails, isPerSeatLicenseEnabled, isThereAnyValidEmail, onCompleted, isValidEmail]);

  function skipClicked() {
    tracking.trackEvent(consts.TRACKING_CATEGORY.ONBOARDING, "skip_invite_button_clicked", "emails");
    onCompleted();
  }

  function changeEmail(id: string, value: string, isLastEmailInput: boolean) {
    trackUserTyping(value, "email_invite_intent", {
      source: "onboarding_invite_screen",
    });

    if (isEmail(value)) {
      trackUserSelfInvited(value, user?.email ?? "");
    }

    setEmails((emails) => {
      const newEmails = { ...emails };
      newEmails[id] = value;
      if (isLastEmailInput && value.length > 0) {
        newEmails[makeid(10)] = "";
      }
      return newEmails;
    });
  }

  function removeEmailField(id: string) {
    if (Object.keys(emails).length === 1) {
      return;
    }

    tracking.trackAnalyticsEvent("invite_to_account_remove_email");

    setEmails((emails) => {
      const newEmails = { ...emails };
      delete newEmails[id];
      return newEmails;
    });
  }

  return (
    <div className={style.pageContainer}>
      <div className={style.contentContainer}>
        <div className={style.titleContainer}>
          <h1 className={classNames(style.title, { [style.largerTitle]: isCreatedFromMonday })}>
            {isCreatedFromMonday ? "Invite people to collaborate in your account" : "Invite people to your account"}
          </h1>
          <span className={style.sectionTitle}>Send invites</span>
        </div>
        <InviteToAccountEmails
          isValidEmail={isValidEmail}
          emails={emails}
          changeEmail={changeEmail}
          isSending={isSending}
          isCreatedFromMonday={isCreatedFromMonday}
          onRemoveEmail={removeEmailField}
        />
        <div className={style.buttonsContainer}>
          <button data-testid="Skip for now" className={style.skipButton} onClick={skipClicked}>
            Remind me later
          </button>
          <div className={style.sendInvitationsWrapper}>
            <StyledButton
              title={invitedCompleted ? "Sent" : "Send Invitations"}
              size={ButtonSize.big}
              customStyle={BUTTON_STYLE}
              loading={isSending}
              loadingTitle="Sending..."
              onClick={inviteClicked}
              enabled={isThereAnyValidEmail && !invitedCompleted}
            />
            <div className={style.invitedMembersMessage}>
              {invitedCompleted && (
                <>
                  <CheckCircledIcon />
                  {invitedAmount} {invitedAmount === 1 ? "member" : "members"} invited
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={style.imageContainer}>
        <div className={style.imagePlaceholder}>
          <img
            alt="canvas-background"
            className={style.inviteToAccountImage}
            src={getPathPrefix("/images/onboarding/invite_to_account/onboarding-invite.png")}
          />
        </div>
      </div>
    </div>
  );
}
